import React, { useEffect, useMemo, useRef } from "react";
import { Textarea } from "../Textarea";
import { Label } from "../Label";
import Checkbox from "../Checkbox";
import type { Platform } from "./types";

const AI_INSTRUCTIONS_LIMIT = 500;

interface ManualContentStepProps {
    generalInstructions: string;
    setGeneralInstructions: (value: string) => void;
    selectedPlatforms: Platform[];
    onPasteTruncationChange?: (isTruncatedAndUnedited: boolean) => void;
    /** Given = the AI Enhance choice is made on this step: ticked, the box takes AI instructions; unticked, the post itself. */
    onAiEnhanceChange?: () => void;
    aiEnhance?: boolean;
}

const ManualContentStep: React.FC<ManualContentStepProps> = ({
    generalInstructions,
    setGeneralInstructions,
    selectedPlatforms,
    onPasteTruncationChange,
    onAiEnhanceChange,
    aiEnhance = false,
}) => {
    const isPasteRef = useRef(false);
    const aiMode = !!onAiEnhanceChange && aiEnhance;
    const { lowestLimit, limitingPlatform } = useMemo(() => {
        if (selectedPlatforms.length === 0) {
            return { lowestLimit: Infinity, limitingPlatform: null };
        }
        const limiting = selectedPlatforms.reduce((min, p) =>
            p.characterLimit < min.characterLimit ? p : min
        );
        return {
            lowestLimit: limiting.characterLimit,
            limitingPlatform: limiting.name,
        };
    }, [selectedPlatforms]);
    const limit = aiMode ? AI_INSTRUCTIONS_LIMIT : lowestLimit;

    useEffect(() => {
        if (limit !== Infinity && generalInstructions.length > limit) {
            setGeneralInstructions(generalInstructions.substring(0, limit));
            // Platform change caused content to be truncated — treat same as paste-truncation
            onPasteTruncationChange?.(true);
        } else if (limit === Infinity || generalInstructions.length < limit) {
            // Content clearly fits within the limit — safe to clear the flag.
            // When length === limit exactly we leave the flag alone: the content may have
            // been previously truncated (e.g. user went back and returned) and we should still
            // show the warning on Continue.
            onPasteTruncationChange?.(false);
        }
    }, [limit]); // eslint-disable-line react-hooks/exhaustive-deps

    const charCount = generalInstructions.length;
    const isFinite = limit !== Infinity;
    const pct = isFinite ? charCount / limit : 0;
    const isAtLimit = isFinite && charCount >= limit;
    const isWarning = pct >= 0.9 && !isAtLimit;

    const counterClass = isAtLimit
        ? "text-red-500"
        : isWarning
        ? "text-amber-500"
        : "text-muted-foreground";

    const counterText = !isFinite
        ? `${charCount} characters`
        : aiMode || selectedPlatforms.length <= 1
            ? `${charCount} / ${limit.toLocaleString()} characters`
            : `${charCount} / ${limit.toLocaleString()} characters (limit set by ${limitingPlatform})`;

    return (
        <div className="space-y-4">
            {onAiEnhanceChange && (
                <div className="flex items-start gap-2">
                    <div className="flex-none mt-0.5">
                        <Checkbox id="ai-enhance" small={true} checked={aiEnhance} onChange={onAiEnhanceChange} />
                    </div>
                    <div className="flex flex-col">
                        <label htmlFor="ai-enhance" className="text-sm font-semibold text-primary cursor-pointer leading-5">
                            AI Enhance
                        </label>
                        <span className="text-sm text-gray-500 leading-4 mt-0.5">
                            AI will enhance or generate the post based on your instructions.
                        </span>
                    </div>
                </div>
            )}
            <div className="flex flex-col space-y-2">
                <Label className="text-primary font-semibold">{aiMode ? "AI Instructions" : "Post Content"}</Label>
            </div>

            <div className="flex flex-col space-y-1">
                <Textarea
                    className={`w-full p-2 border rounded-md min-h-[150px] ${isAtLimit ? "border-red-500 focus:border-red-500 focus-visible:ring-red-500" : isWarning ? "border-amber-400 focus:border-amber-400 focus-visible:ring-amber-400" : ""}`}
                    placeholder={
                        aiMode
                            ? "Provide a few lines of text or bullet points so Marabot can generate the post content for you."
                            : "Enter your post content here..."
                    }
                    value={generalInstructions}
                    onPaste={() => { isPasteRef.current = true; }}
                    onChange={(e) => {
                        const raw = e.target.value;
                        const truncated = isFinite ? raw.substring(0, limit) : raw;
                        if (isPasteRef.current) {
                            isPasteRef.current = false;
                            onPasteTruncationChange?.(isFinite && raw.length > limit);
                        } else {
                            // Keyboard edit — clear any prior truncation flag
                            onPasteTruncationChange?.(false);
                        }
                        setGeneralInstructions(truncated);
                    }}
                />
                <p className={`text-sm mt-1 text-right w-full ${counterClass}`}>
                    {counterText}
                </p>
            </div>
        </div>
    );
};

export default ManualContentStep;
